import {Icon, type IconName} from '@wandb/components';
import clsx from 'clsx';
import React, {useMemo} from 'react';
// TODO: Remove when @wandb/components has a ColorPicker component we can use
//       here. But until then, we need to use the bare Button to get the style
//       control needed for the color picker trigger.
import {Button} from 'react-aria-components';

import {colorStringWithAlpha, type RunColorSource} from '../../../util/colors';
import ColorPicker, {
  type ColorChangeSource,
  type ColorPickerTriggerRenderProps,
} from '../../ColorPicker';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';

type IconSize = React.ComponentProps<typeof Icon>['size'];

type StackedColorDot =
  | {source: 'automatic'}
  | {source: Exclude<RunColorSource, 'automatic'>; color: string};

const RESET_COLOR_BUTTON_CLASS_NAME =
  'flex w-full items-center gap-8 rounded-[4px] border-0 bg-transparent px-6 py-6 text-left font-[inherit] text-[14px] font-semibold leading-5 text-moon-600 hover:bg-moon-100 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-moon-200 dark:hover:bg-moon-800';
const COLOR_PICKER_CONTAINER_STYLE: React.CSSProperties = {left: '-10px'};
const MAX_DESCENDANT_PREVIEW_DOTS = 2;
const MAX_PREVIEW_DOTS = 3;

const UnsetColorDot = () => {
  return (
    <span className="block h-12 w-12 rounded-full border-2 border-solid border-moon-600 opacity-50 dark:border-moon-300" />
  );
};

const InheritedColorDot = ({color}: {color: string}) => {
  return (
    <span
      className="block h-12 w-12 rounded-full border-[3px] border-solid"
      style={{
        backgroundColor: colorStringWithAlpha(color, 0.28),
        borderColor: color,
      }}
    />
  );
};

const StackedColorDots = ({dots}: {dots: StackedColorDot[]}) => {
  const dotSize = 11;
  const dotStep = 5;
  return (
    <span
      className="relative block"
      style={{
        height: dotSize,
        width: dotSize + dotStep * (dots.length - 1),
      }}>
      {dots.map((dot, index) => {
        const isAutomatic = dot.source === 'automatic';
        const isInherited = dot.source === 'inherited';
        return (
          <span
            key={
              dot.source === 'automatic'
                ? dot.source
                : `${dot.source}-${dot.color}`
            }
            className={clsx(
              'absolute top-0 block rounded-full',
              isAutomatic &&
                'border-2 border-solid border-moon-600 opacity-50 dark:border-moon-300'
            )}
            style={{
              backgroundColor: isAutomatic
                ? 'transparent'
                : isInherited
                  ? colorStringWithAlpha(dot.color, 0.28)
                  : dot.color,
              border: isInherited ? `2px solid ${dot.color}` : undefined,
              boxSizing: 'border-box',
              height: dotSize,
              left: index * dotStep,
              width: dotSize,
              zIndex: dots.length - index,
            }}
          />
        );
      })}
    </span>
  );
};

const RowColorPickerTrigger = ({
  activeColor,
  ariaLabel = 'Change run color',
  displayColor = activeColor,
  icon,
  iconSize = 'xl',
  indicator,
  ...handlers
}: ColorPickerTriggerRenderProps & {
  ariaLabel?: string;
  displayColor?: string;
  iconSize?: IconSize;
  indicator?: React.ReactNode;
}) => {
  // Need this to be a button for accessibility, and wandb/components doesn't
  // have an IconButton with custom coloring, yet. Note that when the run is
  // not baselined the icon is an XL circle, so the parent component normalizes
  // the difference in size between the two icons.

  const cls = clsx(
    'flex',
    'items-center',
    'justify-center',
    'cursor-pointer',
    'h-24',
    'w-24',
    'rounded-[var(--border-radius-sm)]',
    'outline-[var(--focus-ring-color)]',
    'focus:bg-moon-200',
    'hover:bg-moon-200'
  );

  return (
    <Button
      style={{color: displayColor}}
      aria-label={ariaLabel}
      data-dd-action-name="Change run color"
      className={cls}
      data-test="color-picker-trigger"
      {...handlers}>
      {indicator ?? (
        <span className="night-aware">
          <Icon icon={icon} size={iconSize} __inverted />
        </span>
      )}
    </Button>
  );
};

type RowColorPickerProps = {
  customRunColor: string;
  customRunColorPreviewColors?: string[];
  customRunColorSource: RunColorSource;
  hasCustomRunColorDescendantColors?: boolean;
  hasParentInheritedColor?: boolean;
  isCustomRunColorClearable?: boolean;
  isColorPickerVisible: boolean;
  isCrossProjectRun?: boolean;
  isParentGroup?: boolean;
  onClearCustomRunColor?: () => void;
  onGradientColorInteractionEnd?: () => void;
  onOpenColorPicker?: () => void;
  onSetCustomRunColor: (
    color: string,
    source?: ColorChangeSource,
    shouldTrack?: boolean
  ) => void;
};

export function RowColorPicker({
  customRunColor,
  customRunColorPreviewColors,
  customRunColorSource,
  hasCustomRunColorDescendantColors,
  hasParentInheritedColor,
  isCustomRunColorClearable,
  isColorPickerVisible,
  isCrossProjectRun,
  isParentGroup,
  onClearCustomRunColor,
  onGradientColorInteractionEnd,
  onOpenColorPicker,
  onSetCustomRunColor,
}: RowColorPickerProps) {
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const resetCustomRunColorLabel =
    hasParentInheritedColor === true
      ? 'Use inherited color'
      : isParentGroup === true
        ? 'Reset group colors'
        : 'Reset color';

  const footer = useMemo(() => {
    if (isCustomRunColorClearable !== true || onClearCustomRunColor == null) {
      return null;
    }

    return (
      <button
        type="button"
        aria-label={resetCustomRunColorLabel}
        className={RESET_COLOR_BUTTON_CLASS_NAME}
        data-test="clear-color-button"
        onClick={event => {
          event.stopPropagation();
          onClearCustomRunColor();
        }}>
        <Icon icon="reset" size="md" />
        <span>{resetCustomRunColorLabel}</span>
      </button>
    );
  }, [
    isCustomRunColorClearable,
    onClearCustomRunColor,
    resetCustomRunColorLabel,
  ]);

  if (!isColorPickerVisible) {
    return null;
  }

  const isUnsetParentGroup =
    isParentGroup === true && customRunColorSource === 'automatic';
  const previewColors = customRunColorPreviewColors ?? [];
  const hasMultiplePreviewColors = previewColors.length > 1;
  const hasDescendantPreviewColors =
    hasCustomRunColorDescendantColors === true && previewColors.length > 0;

  let triggerIcon: IconName = 'circle-filled';
  if (isUnsetParentGroup) {
    triggerIcon = 'circle';
  } else if (customRunColorSource === 'inherited' || isCrossProjectRun) {
    triggerIcon = 'circle-bold';
  }

  let triggerAriaLabel = 'Set custom color';
  if (isUnsetParentGroup) {
    triggerAriaLabel = 'Set group color';
  } else if (customRunColorSource === 'explicit') {
    triggerAriaLabel = 'Change custom color';
  }

  const descendantPreviewDots: StackedColorDot[] = previewColors
    .slice(0, MAX_DESCENDANT_PREVIEW_DOTS)
    .map(color => ({source: 'explicit', color}));
  const previewDots: StackedColorDot[] = previewColors
    .slice(0, MAX_PREVIEW_DOTS)
    .map((color, index) => ({
      color,
      source:
        index === 0 && customRunColorSource === 'inherited'
          ? 'inherited'
          : 'explicit',
    }));

  let triggerIndicator: React.ReactNode;
  if (isUnsetParentGroup && hasDescendantPreviewColors) {
    triggerIndicator = (
      <StackedColorDots
        dots={[{source: 'automatic'}, ...descendantPreviewDots]}
      />
    );
  } else if (hasMultiplePreviewColors) {
    triggerIndicator = <StackedColorDots dots={previewDots} />;
  } else if (isUnsetParentGroup) {
    triggerIndicator = <UnsetColorDot />;
  } else if (customRunColorSource === 'inherited') {
    triggerIndicator = <InheritedColorDot color={customRunColor} />;
  }

  const renderTrigger = (args: ColorPickerTriggerRenderProps) => (
    <RowColorPickerTrigger
      {...args}
      ariaLabel={triggerAriaLabel}
      displayColor={customRunColor}
      indicator={triggerIndicator}
    />
  );

  // TODO: migrate this to @wandb/components ColorPicker when it becomes available.
  //       JIRA: <https://wandb.atlassian.net/browse/WB-29706>
  return (
    <div className="flex shrink-0 items-center w-24 justify-center">
      <ColorPicker
        invertColorsInDarkMode={false}
        activeColor={customRunColor}
        setColor={onSetCustomRunColor}
        colorPaletteOption={colorPaletteOption}
        containerStyle={COLOR_PICKER_CONTAINER_STYLE}
        footer={footer}
        onGradientColorInteractionEnd={onGradientColorInteractionEnd}
        onOpen={onOpenColorPicker}
        trigger={renderTrigger}
        triggerIcon={triggerIcon}
      />
    </div>
  );
}
